/**
 * Step3 のパネル — Audio サンプル (最大 5 候補) の一括生成と一覧表示。
 *
 * ## 「Audio サンプル作成」1 回で音声まで作り切る (計画 9.3.1)
 *
 * ```
 * [Audio サンプル作成] 押下
 *    ↓ ① POST /ssml-tuning/samples   … Bedrock 1 回 → 候補 N 件 (N = 1〜5)
 *    ↓ ② 候補カードを N 枚描画        … 音声は「生成中」表示
 *    ↓ ③ 候補ごとに POST /audio-preview … Polly N 回 (**直列**)
 *    ↓ ④ 合成できたカードから順に <audio> が有効化される
 * 完了 (利用者は上から聴き比べる)
 * ```
 *
 * ⚠️ **② を合成前に描画する** のが要点。全件の合成完了を待って一括描画すると、
 * 数秒〜十数秒のあいだ画面が無反応に見えて「壊れた」と誤解される (R16)。
 *
 * ⚠️ 利用者は SSML を読んで良し悪しを判断できない。「聞こえ方を決める」のが
 * 目的なので、**音の無い候補一覧は判断材料にならない** (計画 9.1.2)。
 * そのため候補を選ばせてから合成する方式は採らない。
 *
 * ## 候補ごとの SSML 編集と Audio 再作成 (計画 8.1 の項目 11・12)
 *
 * ```
 * 候補カードの SSML を編集
 *    ↓ [Audio 再作成] 押下
 *    ↓ POST /audio-preview (**編集済み SSML のみ**)
 *    ↓ その候補の <audio> が差し替わる
 * ```
 *
 * ⚠️ **Bedrock (`/ssml-tuning/samples`) を呼び直さない**。
 * 呼び直すと候補一覧が作り直され、他候補の音声と編集内容まで失われる
 * (かつ Bedrock の再課金が発生する)。再作成は Polly 1 回だけ。
 *
 * ⚠️ 編集値の正源は **本コンポーネント** が持つ (カード内 state にしない)。
 * カード側に持たせると、候補一覧を作り直したときに前回の編集内容が
 * 新しい候補へ残る (React はキーが同じなら再マウントしない)。
 *
 * ## 候補ごとの Audio ダウンロード (計画 8.1 の項目 10)
 *
 * 各カードに「再生」と並べてダウンロードを置く。保存するのは
 * **その時点で再生できる音声** (`useSsmlAudio` が持つ blob URL) で、
 * SSML を編集して再作成した後は **最新の音声** が対象になる。
 *
 * ⚠️ 保存のために `/audio-preview` を呼び直さない。再合成すると Polly が
 * 二重課金になり、かつ「聴いた音」と「保存した音」が別物になる。
 *
 * ファイル名は `{放送タイトル}_{候補タイトル}.mp3` (計画 1.2)。
 * 放送タイトルの元になる **放送テキスト** は Step1 にあるため、
 * ページ経由で `broadcastText` として受け取る。
 *
 * ## ⚠️ 本ステップで実装しないもの
 *
 * 履歴管理・Undo は **対象外**。
 * 「戻したい」場合はテキストエリアの Ctrl+Z を使う (計画 0.7-(1))。
 *
 * ## 設計上の約束
 *
 * - 入力は **Step1 の放送テキスト** (`sourceText`)。SBI-001 で AI 改善案を廃止したため、
 *   後続 SBI で Step2 が辞書適用済 SSML に置き換わるまでは放送テキストをそのまま渡す。
 * - `createSamples` / `previewAudio` は **props で注入**。コンポーネントは
 *   fetch を知らず、テストがネットワークなしで通しの操作を検証できる。
 * - 生成中は再実行を禁止する (二重課金と結果の取り違えを防ぐ)。
 * - **1 件の合成が失敗してもループを止めない** (計画 8.6.2)。
 */
import { Spinner } from '../Spinner';
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { createPortal } from 'react-dom';

import type {
  SsmlSampleCandidate,
  SsmlTuningLogRequest,
  SsmlTuningSamplesResponse,
} from '../../api/ssmlTuning';

import { SampleCandidateCard } from './SampleCandidateCard';
import {
  CREATE_SAMPLES_BUTTON_LABEL as CREATE_BUTTON_LABEL,
  MAX_SAMPLE_CANDIDATE_COUNT,
  STEP3_TITLE,
} from './constants';
import { prepareEditedSsml, sanitizeUnsupportedSsml } from './ssmlDraft';
import { useSsmlAudio } from './useSsmlAudio';
import type { PreviewAudioFn } from './useSsmlAudio';
import { describeApiErrorMessage } from '../../utils/apiErrorMessage';

export interface Step3SamplePanelProps {
  /**
   * SSML 化する放送文 (Step1 で入力した放送テキスト)。
   *
   * 未入力の場合は `null` (または空文字)。その場合は
   * 「Step1 で放送テキストを入力してください」という案内だけを出し、
   * ボタンを押せないようにする (空文字を送って 400 を受けるより親切)。
   */
  sourceText: string | null;
  /**
   * サンプル生成を実行する関数 (`api/ssmlTuning.ts` の `createSamples` を注入)。
   *
   * 例外は本コンポーネントが捕捉してエラー表示に変換するため、
   * 呼び出し側で try/catch する必要はない。
   */
  createSamples: (params: { text: string }) => Promise<SsmlTuningSamplesResponse>;
  /**
   * 音声を合成する関数 (`BroadcastPointsClient.previewAudio` を注入)。
   *
   * ⚠️ 既存 `POST /audio-preview` を **無改変で流用** する (計画 8.3.4)。
   * 話者は Takumi 固定で、Provider / Voice の選択 UI は作らない。
   */
  previewAudio: PreviewAudioFn;
  /**
   * ダウンロードのファイル名に使う **放送テキスト** (Step1 の入力)。
   *
   * `{放送タイトル}_{候補タイトル}.mp3` の前半になる (計画 1.2)。
   *
   * ## ⚠️ `sourceText` と分けている理由
   *
   * 保存名に使うのは **利用者が入力した放送文** であって、加工後の文面 (後続 SBI の
   * 辞書適用済み文面など) ではない。加工後を使うと辞書の内容次第で保存名が変わり、
   * 「同じ放送の音声」をファイル名で束ねられなくなる。
   * 未指定 (Step1 未入力) の場合は `audio_{候補タイトル}.mp3` になる。
   */
  broadcastText?: string | null;
  /**
   * 利用ログを 1 件記録する (`SsmlTuningClient.recordLog` を注入)。
   *
   * 「どんな放送文に対して、AI の SSML をどう直したか」を
   * CloudWatch Logs へ残し、後日 Readme (`/ssml-readme`) を人が直すための
   * 材料にする (docs/ssml-tuning-usage-log.md)。
   *
   * ## ⚠️ 利用者の監視ではない
   *
   * 記録するのは「テキストと SSML の内容」だけで、**誰が操作したか** は
   * 送らない (画面は Cognito の情報を渡さず、サーバも記録しない)。
   *
   * ## ⚠️ 失敗しても操作を止めない実装であること
   *
   * 注入する関数は例外を投げてはいけない (`recordLog` は内部で握り潰す)。
   * ここで throw されると音声の再作成やダウンロードが失敗して見え、
   * 「ログのために本来の機能が壊れる」最悪の形になる。
   *
   * 省略時はログを送らない (未注入の環境やテストでもそのまま動く)。
   */
  recordLog?: (entry: SsmlTuningLogRequest) => void;
  /**
   * 「Audio サンプル作成」ボタン一式の描画先 (UI/UX 改善 Phase2)。
   *
   * 「放送テキストを確認 → そのまま Audio サンプル作成」の流れにするため、ボタンは
   * **Step2 の最下部** に置く。ただし作成処理の状態 (生成中 / 進捗 / 候補 /
   * 二重実行の防止) は本コンポーネントが正源のまま持ちたいので、
   * ボタン部分だけを `createPortal` で Step2 側の差し込み口へ描画する。
   *
   * ⚠️ 状態をページへ持ち上げる方式にしない。持ち上げると生成ロジック
   * (Bedrock 1 回 + Polly 直列) がページと本コンポーネントに分散し、
   * 「生成中は押せない」等の約束を 2 箇所で守る必要が生まれる。
   *
   * - `undefined` (未指定): 従来どおり Step3 内に描画する (単体テスト / 他画面向け)
   * - `null`             : 差し込み口がまだマウントされていない (1 フレームだけ何も出さない)
   * - `HTMLElement`       : その要素の中へ描画する
   */
  actionsContainer?: HTMLElement | null;
}

/** 候補 1 件を音声の状態と対応づけるキー (並び順で一意)。 */
function audioKeyOf(index: number): string {
  /*
   * ⚠️ タイトルをキーにしない。
   * タイトルはサーバ側で一意化されているが、再生成すると同じタイトルが
   * 出ることがあり、「前回の音声が新しい候補に紐づく」事故になる。
   * 並び順 (0 起算) なら候補一覧を作り直した時点で必ず入れ替わる。
   */
  return `candidate-${index}`;
}

export function Step3SamplePanel({
  sourceText,
  createSamples,
  previewAudio,
  broadcastText,
  recordLog,
  actionsContainer,
}: Step3SamplePanelProps): React.JSX.Element {
  const [candidates, setCandidates] = useState<SsmlSampleCandidate[] | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 音声の合成が終わった件数 (進捗表示「(2 / 4 件)」の分子)。
   *
   * ⚠️ 分母は `candidates.length` を使う。定数の 5 を分母にすると
   * 3 件のときに「2 / 5 件」と出て、いつまでも終わらないように見える (9.3.3)。
   */
  const [synthesizedCount, setSynthesizedCount] = useState(0);
  /** 音声の合成に失敗した件数 (完了サマリの内訳に使う)。 */
  const [failedCount, setFailedCount] = useState(0);
  /**
   * 候補ごとの **編集中 SSML** (キー: `audioKeyOf(index)`)。
   *
   * ⚠️ カード内 state にせず、候補一覧と同じライフサイクルで親が持つ。
   * カードに持たせると、再生成時に「前回の編集内容が新しい候補に残る」
   * (React はキーが同じなら再マウントしないため初期化されない)。
   */
  const [ssmlDrafts, setSsmlDrafts] = useState<Record<string, string>>({});
  /**
   * 候補ごとの **現在の音声が作られた SSML** (キー: `audioKeyOf(index)`)。
   *
   * 「編集したが再作成していない」ことをカードに示すために持つ。
   * 無いと利用者は編集後の音声を聴いているつもりで編集前の音声を聴き続ける。
   */
  const [appliedSsmls, setAppliedSsmls] = useState<Record<string, string>>({});
  /**
   * 候補ごとの **再作成の前段エラー** (空 SSML など / キー: `audioKeyOf(index)`)。
   *
   * ⚠️ 全体エラー (`error`) と分ける。1 候補の編集ミスで
   * 画面全体にエラーが出ると、他候補が使えないように見える (依頼のエラー方針)。
   * 合成そのものの失敗は `useSsmlAudio` が候補ごとの状態として持つ。
   */
  const [recreateErrors, setRecreateErrors] = useState<Record<string, string>>({});

  const { audioStates, synthesize, markPending, reset } = useSsmlAudio(previewAudio);

  /**
   * マウント中かどうか。
   *
   * ⚠️ 生成中に画面の「リセット」が押されると本コンポーネントは作り直される
   * (ページが key を変える)。そのまま処理を続けると、破棄済みの候補のために
   * **Polly の合成が走り続けて課金され、利用ログも出てしまう**。
   * アンマウントを検知したら以降の処理を打ち切るために持つ。
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // 放送テキストが無い / 空白だけの状態では生成できない (サーバも 400 を返す)。
  const trimmedSource = (sourceText ?? '').trim();
  const canCreate = trimmedSource !== '';

  /**
   * 利用ログ 1 件を送る (`recordLog` 未注入なら何もしない)。
   *
   * 3 イベントに共通の「放送文」を毎回詰めるためのヘルパ。
   * 各呼び出し箇所で組み立てると、片方だけ詰め忘れて分析できなくなる。
   * ⚠️ SBI-001 で読み上げ要望を廃止したため、`requestText` は送らない (API 型は任意項目のまま残す)。
   *
   * ⚠️ ここでも例外を外へ出さない。ログの失敗で利用者の操作を止めない
   * という約束は、注入側 (`recordLog`) と本関数の両方で守る
   * (テストが素の関数を渡した場合にも壊れないようにするため)。
   */
  function emitLog(entry: Omit<SsmlTuningLogRequest, 'broadcastText'>): void {
    if (recordLog === undefined) {
      return;
    }
    try {
      recordLog({
        ...entry,
        /*
         * ⚠️ 放送文は `broadcastText` (Step1 の入力) を使う。
         * 後続 SBI で `sourceText` が辞書適用済みの文面になっても、
         * 「利用者が何と書いたか」が残るように分けている。
         * 空文字は送らない (サーバ側でキーごと省略され、「入力しなかった」と
         * 「空を入力した」を区別できるようにする)。
         */
        ...(broadcastText !== undefined &&
          broadcastText !== null &&
          broadcastText.trim() !== '' && { broadcastText }),
      });
    } catch {
      // 注入された関数が throw しても操作は続行する (上記の約束)。
    }
  }

  async function handleCreate(): Promise<void> {
    if (!canCreate || isCreating) {
      // 生成中の再押下は無視する (二重課金と結果の取り違えを防ぐ)。
      return;
    }
    setIsCreating(true);
    setError(null);
    // 前回の候補を消してから生成する。残したままだと
    // 「新しい候補なのか前回の候補なのか」が利用者に判別できない。
    setCandidates(null);
    // ⚠️ 前回の音声は必ず revoke する (再実行のたびに URL が積み上がる / R20)。
    reset();
    setSynthesizedCount(0);
    setFailedCount(0);
    try {
      // ① 候補の生成 (Bedrock 1 回)。
      const response = await createSamples({ text: trimmedSource });
      if (!mountedRef.current) {
        // リセット等で破棄された後の応答は捨てる (合成もログも行わない)。
        return;
      }
      /*
       * ⚠️ pitch / 利用不可タグ (<emphasis> / <ruby> など) は Takumi + Polly で使えないため、
       * 画面側でも除去する (タグの一覧は `data/ssmlUnsupportedTags.ts`)
       * (サーバの正規化が正源。ここは古い Lambda / すり抜けに備えた最後の砦)。
       * 表示・自動合成・ログのすべてが **除去後の SSML** を使う。
       */
      const created = response.candidates.map((c) => ({
        ...c,
        ssml: sanitizeUnsupportedSsml(c.ssml),
      }));

      /*
       * ② 候補カードを **合成前に** 描画する (計画 9.3.1 / R16)。
       * 全件の合成完了を待つと、数秒〜十数秒のあいだ画面が無反応に見えて
       * 「壊れた」と誤解され、再押下や離脱につながる。
       */
      setCandidates(created);
      markPending(created.map((_, i) => audioKeyOf(i)));
      /*
       * 編集用の状態を候補と同時に初期化する。
       * ⚠️ ここで作り直さないと、前回の編集内容 / エラーが新しい候補に残る。
       */
      setSsmlDrafts(Object.fromEntries(created.map((c, i) => [audioKeyOf(i), c.ssml])));
      setAppliedSsmls(Object.fromEntries(created.map((c, i) => [audioKeyOf(i), c.ssml])));
      setRecreateErrors({});

      /*
       * 利用ログ: `candidate_generated` を **候補ごとに 1 件** 出す
       * (docs/ssml-tuning-usage-log.md)。
       *
       * ⚠️ 「生成 1 回で 1 件」にはしない。Readme に載せたいのは
       * 「どの狙い (candidateTitle) でどんな SSML を作ったか」であり、
       * まとめて 1 件にすると候補ごとの SSML が失われて
       * タグ候補の抽出ができなくなる。
       *
       * ⚠️ 音声合成 (③) の **前** に出す。合成の成否はこのイベントの
       * 対象ではなく (それは audio_recreated / audio_downloaded が示す)、
       * 合成後に回すと 1 件でも失敗したときに記録が欠ける。
       */
      for (const candidate of created) {
        emitLog({
          eventType: 'candidate_generated',
          candidateTitle: candidate.title,
          generatedSsml: candidate.ssml,
        });
      }

      /*
       * ③ 候補ごとに音声を合成する。
       *
       * ⚠️ **直列 (for + await)**。`Promise.all` で並列にすると Polly の
       * 同時実行数が跳ね上がり、後半の候補だけスロットリングで失敗する (R15)。
       * ⚠️ `synthesize` は失敗しても例外を投げないため、
       * **1 件失敗してもループが止まらない** (計画 8.6.2)。
       */
      let failed = 0;
      for (const [i, candidate] of created.entries()) {
        if (!mountedRef.current) {
          // リセットで破棄されたら残りの候補は合成しない (無駄な Polly 課金を防ぐ)。
          return;
        }
        const ok = await synthesize(audioKeyOf(i), candidate.ssml);
        if (!ok) {
          failed += 1;
        }
        // 進捗は成否を問わず進める (「何件まで処理したか」を示すため)。
        setSynthesizedCount(i + 1);
        setFailedCount(failed);
      }
    } catch (e) {
      if (!mountedRef.current) {
        return;
      }
      // サーバのメッセージ (「モデルアクセスを有効化してください」等) を
      // そのまま出す。運用者が次の行動を判断できるようにするため。
      // ⚠️ ここに来るのは **候補生成 (①) の失敗** のみ。
      //    音声の失敗は候補ごとの状態として扱う (全体エラーにしない)。
      setError(describeApiErrorMessage(e, 'Audio サンプルの作成に失敗しました'));
    } finally {
      if (mountedRef.current) {
        setIsCreating(false);
      }
    }
  }

  /**
   * 失敗した候補の音声だけを作り直す (計画 9.3.2)。
   *
   * ⚠️ 全体をやり直させない。成功した候補まで再合成すると
   * 無駄な Polly 課金が発生し、待ち時間も伸びる。
   */
  async function handleRetryAudio(index: number): Promise<void> {
    /*
     * ⚠️ 再試行に使うのも **編集後の SSML**。
     * 候補の元 SSML を使うと、「構文エラーを直したのに直す前の SSML で
     * 再試行され、同じエラーが出続ける」ことになる。
     */
    const key = audioKeyOf(index);
    const ok = await recreateAudio(index, ssmlDrafts[key] ?? '');
    // 完了サマリの失敗件数を実態に合わせる (再試行で成功したら 1 件減らす)。
    if (ok) {
      setFailedCount((prev) => (prev > 0 ? prev - 1 : 0));
    }
  }

  /**
   * 編集した SSML で **その候補の音声だけ** を作り直す (計画 8.1 の項目 12)。
   *
   * ## ⚠️ Bedrock を呼び直さない
   *
   * ここでは `/audio-preview` (Polly) しか呼ばない。
   * `/ssml-tuning/samples` を呼び直すと候補一覧そのものが作り直され、
   * 他候補の音声と編集内容が消え、Bedrock の再課金も発生する。
   *
   * @returns 音声を差し替えられたら `true`
   */
  async function recreateAudio(index: number, rawSsml: string): Promise<boolean> {
    const key = audioKeyOf(index);
    /*
     * 送る前に最小整形する (`<speak>` 補完 / 空・長すぎの検出)。
     * 空や長すぎを送っても 400 が返るだけで理由が伝わらないため、
     * 画面側で理由を示す。構文チェックはしない (Polly の 400 に委ねる)。
     */
    const prepared = prepareEditedSsml(rawSsml);
    if (!prepared.ok) {
      // ⚠️ **この候補だけ** にエラーを出す (他候補の表示には触れない)。
      setRecreateErrors((prev) => ({ ...prev, [key]: prepared.error }));
      return false;
    }
    // 前回の前段エラーは消す (直したのに古い指摘が残ると混乱する)。
    setRecreateErrors((prev) => {
      if (prev[key] === undefined) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });

    /*
     * ⚠️ 補完後の SSML をテキストエリアへ戻す。
     * 画面の表示と実際に合成した内容がずれると、
     * 「この SSML で作った音声」という対応関係が崩れる。
     */
    setSsmlDrafts((prev) => ({ ...prev, [key]: prepared.ssml }));

    /*
     * `synthesize` は失敗しても例外を投げず、候補ごとの状態を `error` にする。
     * よって **他候補には一切影響しない** (計画 8.6.2 / 依頼のエラー方針)。
     */
    const ok = await synthesize(key, prepared.ssml);
    if (ok && mountedRef.current) {
      // 成功時だけ「音声の元 SSML」を更新する。失敗時に更新すると
      // 「編集が反映済み」と表示されるのに音声は古いままになる。
      setAppliedSsmls((prev) => ({ ...prev, [key]: prepared.ssml }));

      /*
       * 利用ログ: `audio_recreated` (**Readme 改善の最重要シグナル**)。
       *
       * 「AI の SSML では足りず、人が手で直した」ことがここに現れる。
       * `generatedSsml` (AI が出した元) と `editedSsml` (直した後) を
       * **両方** 残すのが要点で、差分から Readme 未掲載の改善パターンや
       * 頻出する編集を抽出する (docs/ssml-tuning-usage-log.md)。
       *
       * ⚠️ 合成が成功したときだけ出す。失敗した SSML は「使えなかった案」で
       * あり、これを混ぜると「利用者が採用した編集」の分析が汚れる。
       */
      const candidate = candidates?.[index];
      emitLog({
        eventType: 'audio_recreated',
        audioRecreated: true,
        ...(candidate !== undefined && {
          candidateTitle: candidate.title,
          // ⚠️ AI の元 SSML は候補の値を使う (`appliedSsmls` ではない)。
          //   再作成を 2 回以上行うと `appliedSsmls` は「前回の編集後」に
          //   なっており、AI 出力との差分が取れなくなる。
          generatedSsml: candidate.ssml,
        }),
        editedSsml: prepared.ssml,
      });
    }
    return ok;
  }

  /**
   * ダウンロードが押されたことを記録する (`audio_downloaded`)。
   *
   * 「実際に保存された = 現場で使える品質だった候補」を示す指標で、
   * `candidate_generated` との比率が Readme 掲載の優先度判断になる。
   *
   * ⚠️ ダウンロード自体は `<a download>` がブラウザ機能として行う。
   * ここでログを送るだけで、**保存処理には一切介入しない**
   * (介入すると保存が失敗し得るし、`/audio-preview` の再呼び出しも招く)。
   *
   * ⚠️ `editedSsml` には **いま画面にある SSML** を入れる。編集後に
   * 再作成して保存した場合、保存された音声はその SSML から作られている。
   */
  function handleDownloaded(index: number): void {
    const candidate = candidates?.[index];
    const key = audioKeyOf(index);
    emitLog({
      eventType: 'audio_downloaded',
      audioDownloaded: true,
      ...(candidate !== undefined && {
        candidateTitle: candidate.title,
        generatedSsml: candidate.ssml,
      }),
      ...(appliedSsmls[key] !== undefined && { editedSsml: appliedSsmls[key] }),
    });
  }

  /** 候補ごとの SSML 編集を受け取る (正源は本コンポーネントが持つ)。 */
  function handleSsmlDraftChange(index: number, ssml: string): void {
    setSsmlDrafts((prev) => ({ ...prev, [audioKeyOf(index)]: ssml }));
  }

  /*
   * 「Audio サンプル作成」ボタン・押せない理由・作成エラー。
   *
   * ⚠️ 作成エラーもボタンと一緒に置く。ボタンは Step2 側にあるため、
   * エラーだけ Step3 (画面下段) に出すと押した位置から見えず失敗に気づけない。
   */
  const createActions = (
    <>
      <div className="ssml-tuning-step__actions">
        <button
          type="button"
          className="ssml-tuning-button ssml-tuning-button--primary"
          data-testid="ssml-tuning-samples-button"
          onClick={() => void handleCreate()}
          disabled={!canCreate || isCreating}
          /* 押せない理由 (放送テキストが未入力) をボタンと結び付ける (スクリーンリーダー向け)。 */
          aria-describedby={!canCreate ? 'ssml-tuning-samples-disabled-reason' : undefined}
        >
          {/*
              ⚠️ 生成中は無効化する (計画 9.5.5)。押し直しを許すと
              Bedrock + Polly が二重に走り、どちらの結果が表示されているか
              利用者にも分からなくなる。
            */}
          {isCreating ? (
            <>
              <Spinner />
              {`${CREATE_BUTTON_LABEL}中…`}
            </>
          ) : (
            CREATE_BUTTON_LABEL
          )}
        </button>
        {/*
            ⚠️ 旧「AI で SSML を作成し、続けて音声を最大 5 件作成します」の説明文は
            UI/UX 改善 Phase4 で削除した (ボタン名称に「SSMLタグでチューニング」を含めたため)。
            最大件数は Step3 見出しの「(最大 5 件)」で伝わる。
          */}
      </div>

      {/*
          放送テキストが未入力のときは「なぜ押せないか」を明示する。
          無効なボタンだけを出すと、利用者は原因が分からず操作に詰まる。
        */}
      {!canCreate && (
        <p
          className="ssml-tuning-step__disabled-reason"
          id="ssml-tuning-samples-disabled-reason"
          data-testid="ssml-tuning-step3-empty"
        >
          Step1 で放送テキストを入力すると「{CREATE_BUTTON_LABEL}」を実行できます。
        </p>
      )}

      {error !== null && (
        <p className="ssml-tuning-error" role="alert" data-testid="ssml-tuning-samples-error">
          {error}
        </p>
      )}
    </>
  );

  /*
   * 差し込み口 (Step2 最下部) が指定されていればそこへ描画する。
   * React のイベントは Portal でも React ツリーに沿って伝播するため、
   * onClick などの挙動は Step3 内に置いた場合と変わらない。
   */
  const portaledActions =
    actionsContainer !== undefined && actionsContainer !== null
      ? createPortal(createActions, actionsContainer)
      : null;

  return (
    <>
      {portaledActions}
      <section
        className="ssml-tuning-step"
        data-testid="ssml-tuning-step3"
        aria-labelledby="ssml-tuning-step3-title"
      >
        {/*
          見出し行 (UI/UX 改善 Phase4):
            Step3 Audio サンプル（最大 5 件）｜4件の候補（音声3件 / 失敗1件）
          旧: 見出しの下に完了サマリを別の行で出していた。1 行へ統合して縦幅を詰める。
          ⚠️ サマリは h3 の外に置く (見出しのアクセシブルネームを変えない)。
        */}
        <div className="ssml-tuning-step__title-row">
          <h3 className="ssml-tuning-step__title" id="ssml-tuning-step3-title">
            {/*
              ⚠️ 「最大」を必ず付ける (計画 9.2 / R18)。
              AI は狙いを作り分けられない場合に 3〜4 件へ減らすため、
              「5 件」と書くと「聞いていた件数より少ない」という不信につながる。
            */}
            {/*
              見出しで Step3 の責務 (放送テキスト → SSML チューニング用 Audio サンプル) を示す。
            */}
            {STEP3_TITLE}（最大 {MAX_SAMPLE_CANDIDATE_COUNT} 件）
          </h3>
          {/*
            進捗 / 完了サマリ (計画 9.3.3)。

            ⚠️ 分母・件数は必ず `candidates.length` を使う (定数の 5 を書かない)。
            候補は 3〜5 件で変動するため、「5」と決め打ちすると
            「2 / 5 件」で止まったように見える (計画 9.2 / R5 / R18)。
          */}
          {candidates !== null &&
            (isCreating ? (
              <span className="ssml-tuning-step__summary" data-testid="ssml-tuning-audio-progress">
                ｜音声を生成しています（{synthesizedCount} / {candidates.length}件）
              </span>
            ) : (
              <span className="ssml-tuning-step__summary" data-testid="ssml-tuning-samples-count">
                {/*
                  候補数と成功数を同じ場所で確認できるよう、内訳は **常に** 出す
                  (最終仕上げ: 「｜4件の候補（音声4件 / 失敗0件）」)。
                  旧仕様 (失敗 0 件なら内訳を省略) だと、成功数を確かめるのに
                  カードを 1 枚ずつ見る必要があった。
                */}
                ｜{candidates.length}件の候補（音声{candidates.length - failedCount}件 / 失敗
                {failedCount}件）
              </span>
            ))}
        </div>

        {/* 作成ボタン一式 (差し込み口の指定が無いときだけここに出す)。 */}
        {actionsContainer === undefined && createActions}

        {/*
        作成前の案内 (UI/UX 改善 Phase2)。
        ボタンが Step2 へ移ったため、Step3 が空欄だと「何をすれば埋まるか」が分からない。
      */}
        {actionsContainer !== undefined && candidates === null && !isCreating && (
          <p className="ssml-tuning-step__placeholder" data-testid="ssml-tuning-step3-placeholder">
            Step2 の「{CREATE_BUTTON_LABEL}」を実行すると、ここに候補と音声が表示されます。
          </p>
        )}

        {candidates !== null && (
          <>
            <ul className="ssml-sample-list" data-testid="ssml-tuning-samples-list">
              {candidates.map((candidate, i) => (
                <SampleCandidateCard
                  // タイトルはサーバ側で一意化されているためキーに使える
                  // (重複時は連番が付く / 計画リスク R2)。
                  key={candidate.title}
                  candidate={candidate}
                  index={i + 1}
                  audio={audioStates[audioKeyOf(i)]}
                  onRetryAudio={() => void handleRetryAudio(i)}
                  /*
                  SSML 編集と Audio 再作成は **候補カード内で完結** させる
                  (別画面・モーダルを出さない / 依頼の UI 方針)。
                  値と更新関数を親から渡し、状態の正源をここに集約する。
                */
                  ssmlDraft={ssmlDrafts[audioKeyOf(i)] ?? candidate.ssml}
                  onSsmlDraftChange={(ssml) => handleSsmlDraftChange(i, ssml)}
                  onRecreateAudio={() =>
                    void recreateAudio(i, ssmlDrafts[audioKeyOf(i)] ?? candidate.ssml)
                  }
                  appliedSsml={appliedSsmls[audioKeyOf(i)] ?? candidate.ssml}
                  // ⚠️ この候補のエラーだけを渡す (他候補のカードには出さない)。
                  recreateError={recreateErrors[audioKeyOf(i)] ?? null}
                  /*
                  ダウンロード名の前半に使う放送テキスト。
                  ⚠️ `sourceText` ではなく **利用者が入力した放送文** を渡す
                  (AI の言い換えで保存名が変わると、同じ放送の音声を束ねられない)。
                */
                  broadcastText={broadcastText}
                  /*
                  利用ログ (`audio_downloaded`)。
                  ⚠️ カードは「押された」ことだけを通知し、送信内容は親が組む
                  (カードに放送文を渡すと、保存名以外の用途で
                  Step1 の入力に依存する箇所が増えて追いにくくなる)。
                */
                  onDownloaded={() => handleDownloaded(i)}
                />
              ))}
            </ul>
          </>
        )}
      </section>
    </>
  );
}
